import React, { useState, useEffect } from 'react';
import { 
  Box, Typography, Button, Paper, Table, TableBody, TableCell, TableHead, TableRow, 
  IconButton, Chip, Drawer, TextField, FormControl, InputLabel, Select, MenuItem, 
  LinearProgress, Grid, Grow
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import axios from 'axios';
import API_URL from '../config';
import Loading from '../components/Loading';

function KPIs() {
  const [kpis, setKpis] = useState([]);
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saveLoading, setSaveLoading] = useState(false);
  
  const [openDrawer, setOpenDrawer] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentKPI, setCurrentKPI] = useState({ 
    user_id: '', project_id: '', name: '', description: '', 
    value: 0, target: 100, unit: 'Count', status: 'Pending', period: '' 
  });

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [kRes, uRes, pRes] = await Promise.all([
        axios.get(`${API_URL}/kpis`, { headers }),
        axios.get(`${API_URL}/setup/users`, { headers }),
        axios.get(`${API_URL}/projects`, { headers })
      ]);
      setKpis(kRes.data);
      setUsers(uRes.data);
      setProjects(pRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSave = async () => {
    setSaveLoading(true);
    try {
      const payload = {
        ...currentKPI,
        project_id: currentKPI.project_id || null
      };
      
      if (editMode) {
        await axios.put(`${API_URL}/kpis/${currentKPI.id}`, payload, { headers });
      } else {
        await axios.post(`${API_URL}/kpis`, payload, { headers });
      }
      setOpenDrawer(false);
      fetchData();
    } catch (err) {
      alert('Error saving KPI');
    } finally {
      setSaveLoading(false);
    }
  };

  const handleEdit = (kpi) => {
    setCurrentKPI(kpi);
    setEditMode(true);
    setOpenDrawer(true);
  };

  const handleAdd = () => {
    setCurrentKPI({ 
      user_id: '', project_id: '', name: '', description: '', 
      value: 0, target: 100, unit: 'Count', status: 'Pending', period: '' 
    });
    setEditMode(false);
    setOpenDrawer(true);
  };

  const handleDelete = async (id) => {
    if(!window.confirm('Are you sure?')) return;
    try {
      await axios.delete(`${API_URL}/kpis/${id}`, { headers });
      fetchData();
    } catch (err) {
      alert('Error deleting KPI');
    }
  };

  const getProgressColor = (value, target) => {
    const percentage = (value / target) * 100;
    if (percentage >= 100) return 'success';
    if (percentage >= 70) return 'primary';
    return 'warning';
  };

  if (loading) return <Loading message="Loading KPIs..." />;

  return (
    <Box sx={{ width: '100%', overflowX: 'hidden', boxSizing: 'border-box', pb: 6, px: { xs: 1, md: 3 }, bgcolor: '#f4f6f8', minHeight: '100vh' }}>
      {/* Header Section */}
      <Grow in={true} timeout={500}>
        <Box sx={{ 
            mb: 4, 
            mt: 2, 
            p: 4, 
            borderRadius: 4, 
            background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)', 
            color: 'white',
            boxShadow: '0 10px 30px rgba(30, 60, 114, 0.2)',
            position: 'relative',
            overflow: 'hidden'
        }}>
            {/* Background Decorative Circles */}
            <Box sx={{ position: 'absolute', top: -50, right: -50, width: 200, height: 200, borderRadius: '50%', background: 'rgba(255,255,255,0.05)' }} />
            <Box sx={{ position: 'absolute', bottom: -100, right: 100, width: 250, height: 250, borderRadius: '50%', background: 'rgba(255,255,255,0.05)' }} />

            <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, gap: 3 }}>
                <Box>
                    <Typography variant="h3" fontWeight="800" sx={{ letterSpacing: -1, color: 'white', fontSize: { xs: '2rem', md: '2.5rem' }, mb: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
                        <TrendingUpIcon fontSize="large" sx={{ color: 'white' }} /> Employee KPIs
                    </Typography>
                    <Typography variant="subtitle1" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                        Manage performance metrics and tracking
                    </Typography>
                </Box>
                
                <Button 
                    variant="contained" 
                    startIcon={<AddIcon />} 
                    onClick={handleAdd}
                    sx={{ whiteSpace: 'nowrap', borderRadius: 3, bgcolor: 'white', color: '#1e3c72', fontWeight: 'bold', boxShadow: '0 4px 14px rgba(0,0,0,0.1)', '&:hover': { bgcolor: '#f0f0f0' }, px: 3, py: 1.5 }}
                >
                    Add KPI
                </Button>
            </Box>
        </Box>
      </Grow>

      <Grow in={true} timeout={800}>
      <Paper elevation={0} sx={{ borderRadius: 3, overflow: 'hidden', border: '1px solid', borderColor: 'divider', boxShadow: '0 4px 20px rgba(0,0,0,0.04)', bgcolor: 'white' }}>
        <Box sx={{ overflowX: 'auto' }}>
        <Table sx={{ minWidth: 800, '& .MuiTableCell-root': { py: 2.5 } }}>
            <TableHead sx={{ bgcolor: '#f8f9fa' }}>
                <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', color: '#1e3c72', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: 0.5 }}>KPI Name</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', color: '#1e3c72', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: 0.5 }}>Employee</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', color: '#1e3c72', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: 0.5 }}>Project</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', color: '#1e3c72', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: 0.5 }}>Progress</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', color: '#1e3c72', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: 0.5 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', color: '#1e3c72', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: 0.5 }}>Period</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', color: '#1e3c72', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: 0.5 }}>Actions</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
            {kpis.map(k => (
                <TableRow key={k.id} hover>
                    <TableCell>
                        <Typography fontWeight="medium">{k.name}</Typography>
                        <Typography variant="caption" color="text.secondary">{k.unit}</Typography>
                    </TableCell>
                    <TableCell>{k.user_name}</TableCell>
                    <TableCell>{k.project_name || '-'}</TableCell>
                    <TableCell sx={{ width: 200 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ flexGrow: 1 }}>
                                <LinearProgress 
                                    variant="determinate" 
                                    value={k.target > 0 ? Math.min((k.value / k.target) * 100, 100) : 0} 
                                    color={getProgressColor(k.value, k.target)}
                                    sx={{ height: 8, borderRadius: 4 }}
                                />
                            </Box>
                            <Typography variant="caption" fontWeight="bold">
                                {k.value}/{k.target}
                            </Typography>
                        </Box>
                    </TableCell>
                    <TableCell>
                        <Chip 
                            label={k.status} 
                            size="small" 
                            color={k.status === 'Achieved' ? 'success' : k.status === 'Failed' ? 'error' : 'default'} 
                            variant={k.status === 'Pending' ? 'outlined' : 'filled'}
                        />
                    </TableCell>
                    <TableCell>{k.period}</TableCell>
                    <TableCell align="right">
                        <IconButton size="small" onClick={() => handleEdit(k)} color="primary">
                            <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => handleDelete(k.id)} color="error">
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    </TableCell>
                </TableRow>
            ))}
            {kpis.length === 0 && (
                <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                        No KPIs found.
                    </TableCell>
                </TableRow>
            )}
            </TableBody>
        </Table>
        </Box>
      </Paper>
      </Grow>

      <Drawer
        anchor="right"
        open={openDrawer}
        onClose={() => setOpenDrawer(false)}
        PaperProps={{ sx: { width: { xs: '100%', md: 450 }, p: 0, borderTopLeftRadius: { md: 24 }, borderBottomLeftRadius: { md: 24 } } }}
      >
          <Box sx={{ 
              p: 3, 
              background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)', 
              color: 'white', 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
          }}>
              <Typography variant="h6" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {editMode ? <EditIcon /> : <AddIcon />} 
                  {editMode ? 'Edit KPI' : 'New KPI'}
              </Typography>
              <IconButton onClick={() => setOpenDrawer(false)} sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.1)', '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' } }}>
                  <CloseIcon />
              </IconButton>
          </Box>
          
          <Box sx={{ p: 4, display: 'flex', flexDirection: 'column', gap: 3, bgcolor: '#fbfbfb', flexGrow: 1 }}>
              <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider', display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                  <FormControl fullWidth size="small">
                      <InputLabel>Employee</InputLabel>
                      <Select 
                        value={currentKPI.user_id} 
                        label="Employee" 
                        onChange={e => setCurrentKPI({...currentKPI, user_id: e.target.value})}
                        disabled={editMode} 
                        sx={{ borderRadius: 2 }}
                      >
                          {users.map(u => <MenuItem key={u.id} value={u.id}>{u.full_name}</MenuItem>)}
                      </Select>
                  </FormControl>

                  <FormControl fullWidth size="small">
                      <InputLabel>Project (Optional)</InputLabel>
                      <Select 
                        value={currentKPI.project_id || ''} 
                        label="Project (Optional)" 
                        onChange={e => setCurrentKPI({...currentKPI, project_id: e.target.value})}
                        sx={{ borderRadius: 2 }}
                      >
                          <MenuItem value=""><em>None</em></MenuItem>
                          {projects.map(p => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
                      </Select>
                  </FormControl>

                  <TextField 
                    label="KPI Name" 
                    size="small"
                    fullWidth 
                    value={currentKPI.name} 
                    onChange={e => setCurrentKPI({...currentKPI, name: e.target.value})} 
                    InputProps={{ sx: { borderRadius: 2 } }}
                  />

                  <TextField 
                    label="Description" 
                    size="small"
                    fullWidth 
                    multiline 
                    rows={3} 
                    value={currentKPI.description || ''} 
                    onChange={e => setCurrentKPI({...currentKPI, description: e.target.value})} 
                    InputProps={{ sx: { borderRadius: 2 } }}
                  />
              </Paper>

              <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider', display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                  <Grid container spacing={2}>
                      <Grid item xs={6}>
                          <TextField 
                            label="Current Value" 
                            size="small"
                            type="number" 
                            fullWidth 
                            value={currentKPI.value} 
                            onChange={e => setCurrentKPI({...currentKPI, value: e.target.value})} 
                            InputProps={{ sx: { borderRadius: 2 } }}
                          />
                      </Grid>
                      <Grid item xs={6}>
                          <TextField 
                            label="Target Value" 
                            size="small"
                            type="number" 
                            fullWidth 
                            value={currentKPI.target} 
                            onChange={e => setCurrentKPI({...currentKPI, target: e.target.value})} 
                            InputProps={{ sx: { borderRadius: 2 } }}
                          />
                      </Grid>
                  </Grid>

                  <Grid container spacing={2}>
                      <Grid item xs={6}>
                          <FormControl fullWidth size="small">
                              <InputLabel>Unit</InputLabel>
                              <Select 
                                value={currentKPI.unit || 'Count'} 
                                label="Unit" 
                                onChange={e => setCurrentKPI({...currentKPI, unit: e.target.value})}
                                sx={{ borderRadius: 2 }}
                              >
                                  <MenuItem value="Count">Count</MenuItem>
                                  <MenuItem value="Percentage">Percentage</MenuItem>
                                  <MenuItem value="Currency">Currency</MenuItem>
                                  <MenuItem value="Hours">Hours</MenuItem>
                              </Select>
                          </FormControl>
                      </Grid>
                      <Grid item xs={6}>
                          <FormControl fullWidth size="small">
                              <InputLabel>Status</InputLabel>
                              <Select 
                                value={currentKPI.status || 'Pending'} 
                                label="Status" 
                                onChange={e => setCurrentKPI({...currentKPI, status: e.target.value})}
                                sx={{ borderRadius: 2 }}
                              >
                                  <MenuItem value="Pending">Pending</MenuItem>
                                  <MenuItem value="In Progress">In Progress</MenuItem>
                                  <MenuItem value="Achieved">Achieved</MenuItem>
                                  <MenuItem value="Failed">Failed</MenuItem>
                              </Select>
                          </FormControl>
                      </Grid>
                  </Grid>

                  <TextField 
                    label="Period" 
                    size="small"
                    fullWidth 
                    placeholder="e.g. Q1 2025" 
                    value={currentKPI.period || ''} 
                    onChange={e => setCurrentKPI({...currentKPI, period: e.target.value})} 
                    InputProps={{ sx: { borderRadius: 2 } }}
                  />
              </Paper>

              <Button 
                variant="contained" 
                size="large" 
                onClick={handleSave} 
                sx={{ 
                    mt: 'auto', 
                    borderRadius: 3, 
                    py: 1.5, 
                    fontWeight: 'bold',
                    boxShadow: '0 4px 12px rgba(33, 150, 243, 0.3)',
                    background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                    '&:hover': { background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)' }
                }} 
                disabled={saveLoading}
              >
                  {saveLoading ? <LinearProgress sx={{ width: '50%', color: 'white' }} /> : (editMode ? 'Update KPI' : 'Create KPI')}
              </Button>
          </Box>
      </Drawer>
    </Box>
  );
}

export default KPIs;
